import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface DocumentType {
  id: number;
  name: string;
  l10n_pe_vat_code: string;
}

interface State {
  id: number;
  name: string;
  code: string;
}

interface City {
  id: number;
  name: string;
  l10n_pe_code: string;
}

interface District {
  id: number;
  name: string;
  code: string;
}

const CustomerForm: React.FC = () => {
  const { token } = useAuth();
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);

  // Form fields
  const [selectedDocType, setSelectedDocType] = useState<DocumentType | null>(null);
  const [docNumber, setDocNumber] = useState('');
  const [name, setName] = useState('');
  const [street, setStreet] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [selectedState, setSelectedState] = useState<State | null>(null);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<District | null>(null);
  const [companyType, setCompanyType] = useState<'person' | 'company'>('person');

  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [docNumberError, setDocNumberError] = useState('');
  const [isAutoPopulating, setIsAutoPopulating] = useState(false);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoadingData(true);
      await Promise.all([fetchDocumentTypes(), fetchStates()]);
      setLoadingData(false);
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (selectedState && !isAutoPopulating) {
      fetchCities(selectedState.id);
      setSelectedCity(null);
      setSelectedDistrict(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState]);

  useEffect(() => {
    if (selectedCity && !isAutoPopulating) {
      fetchDistricts(selectedCity.id);
      setSelectedDistrict(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity]);

  useEffect(() => {
    if (selectedDocType) {
      const docCode = selectedDocType.l10n_pe_vat_code;
      if (docCode === '6') {
        setCompanyType('company');
      } else {
        setCompanyType('person');
      }
    }
  }, [selectedDocType]);

  // Validación del número de documento
  const validateDocNumber = (value: string, docType: DocumentType | null): string => {
    if (!docType) return '';

    const docCode = docType.l10n_pe_vat_code;

    // Validar que solo contenga números
    if (!/^\d*$/.test(value)) {
      return 'Solo se permiten números';
    }

    // Validar longitud según tipo de documento
    if (docCode === '1') { // DNI
      if (value.length > 0 && value.length !== 8) {
        return 'El DNI debe tener exactamente 8 dígitos';
      }
    } else if (docCode === '6') { // RUC
      if (value.length > 0 && value.length !== 11) {
        return 'El RUC debe tener exactamente 11 dígitos';
      }
    } else if (docCode === '4') { // Carnet de extranjería
      if (value.length > 0 && value.length !== 12) {
        return 'El Carnet de extranjería debe tener exactamente 12 dígitos';
      }
    }

    return '';
  };

  const handleDocNumberChange = (value: string) => {
    const docCode = selectedDocType?.l10n_pe_vat_code;

    // Limitar longitud máxima según tipo de documento
    let maxLength = 20; // Por defecto
    if (docCode === '1') maxLength = 8;  // DNI
    else if (docCode === '6') maxLength = 11; // RUC
    else if (docCode === '4') maxLength = 12; // Carnet de extranjería

    // No permitir más caracteres de los permitidos
    if (value.length > maxLength) {
      return;
    }

    setDocNumber(value);
    const errorMsg = validateDocNumber(value, selectedDocType);
    setDocNumberError(errorMsg);
  };

  const fetchDocumentTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/odoo/document_types`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Document types loaded:', response.data.document_types);
      setDocumentTypes(response.data.document_types || []);
    } catch (err) {
      console.error('Error fetching document types:', err);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API_URL}/odoo/states`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStates(response.data.states || []);
    } catch (err) {
      console.error('Error fetching states:', err);
    }
  };

  const fetchCities = async (stateId: number) => {
    try {
      const response = await axios.get(`${API_URL}/odoo/cities?state_id=${stateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCities(response.data.cities || []);
    } catch (err) {
      console.error('Error fetching cities:', err);
    }
  };

  const fetchDistricts = async (cityId: number) => {
    try {
      const response = await axios.get(`${API_URL}/odoo/districts?city_id=${cityId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDistricts(response.data.districts || []);
    } catch (err) {
      console.error('Error fetching districts:', err);
    }
  };

  const handleSearchDocument = async () => {
    if (!docNumber || !selectedDocType) {
      setError('Debe seleccionar tipo de documento e ingresar el número');
      return;
    }

    // Validar formato del documento antes de buscar
    const validationError = validateDocNumber(docNumber, selectedDocType);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSearching(true);
    setError('');

    try {
      const response = await axios.get(
        `${API_URL}/odoo/search_document/${docNumber}?doc_type=${selectedDocType.l10n_pe_vat_code}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.error) {
        setError(response.data.message || 'No se encontraron datos');
      } else if (response.data.registro) {
        setError('Este documento ya está registrado en el sistema');
      } else if (response.data.data?.success && response.data.data.data) {
        const data = response.data.data.data;

        // Autocompletar datos
        if (data.razonSocial) {
          setName(data.razonSocial);
        } else if (data.names) {
          setName(data.names);
        }

        if (data.direccion && data.direccion !== '-') {
          setStreet(data.direccion);
        }

        // Autocompletar ubicación
        if (data.department_id) {
          const state = states.find(s => s.id === data.department_id);
          if (state) {
            // Activar modo auto-populate
            setIsAutoPopulating(true);
            setSelectedState(state);

            // Cargar ubicaciones de forma secuencial
            const loadLocations = async () => {
              // Cargar ciudades y obtenerlas directamente de la respuesta
              try {
                const citiesResponse = await axios.get(
                  `${API_URL}/odoo/cities?state_id=${data.department_id}`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );

                if (citiesResponse.data.status === 'success') {
                  const loadedCities = citiesResponse.data.cities;
                  setCities(loadedCities);

                  if (data.province_id) {
                    const city = loadedCities.find((c: City) => c.id === data.province_id);
                    if (city) {
                      setSelectedCity(city);

                      // Cargar distritos y obtenerlos directamente de la respuesta
                      try {
                        const districtsResponse = await axios.get(
                          `${API_URL}/odoo/districts?city_id=${data.province_id}`,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );

                        if (districtsResponse.data.status === 'success') {
                          const loadedDistricts = districtsResponse.data.districts;
                          setDistricts(loadedDistricts);

                          if (data.district_id) {
                            const district = loadedDistricts.find((d: District) => d.id === data.district_id);
                            if (district) {
                              setSelectedDistrict(district);
                            }
                          }
                        }
                      } catch (districtErr) {
                        console.error('Error cargando distritos:', districtErr);
                      }
                    }
                  }
                }
              } catch (cityErr) {
                console.error('Error cargando ciudades:', cityErr);
              } finally {
                // Desactivar modo auto-populate después de terminar
                setIsAutoPopulating(false);
              }
            };

            loadLocations();
          }
        }

        setSuccess('Datos encontrados correctamente');
      } else {
        // Caso donde no se encontraron datos automáticos
        setSuccess(response.data.message || 'Por favor, ingrese los datos manualmente');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al buscar el documento');
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedDocType || !docNumber || !name) {
      setError('Debe completar los campos obligatorios: Tipo de documento, Número y Nombre');
      return;
    }

    // Validar formato del documento antes de enviar
    const validationError = validateDocNumber(docNumber, selectedDocType);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        l10n_latam_identification_type_id: selectedDocType.id,
        doc_number: docNumber,
        name: name,
        street: street || undefined,
        phone: phone || undefined,
        email: email || undefined,
        l10n_pe_district: selectedDistrict?.id || undefined,
        city_id: selectedCity?.id || undefined,
        state_id: selectedState?.id || undefined,
        country_id: 173, // Perú
        company_type: companyType,
      };

      const response = await axios.post(`${API_URL}/odoo/customer`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess(response.data.message || 'Cliente creado exitosamente');

      // Resetear formulario
      setSelectedDocType(null);
      setDocNumber('');
      setName('');
      setStreet('');
      setPhone('');
      setEmail('');
      setSelectedState(null);
      setSelectedCity(null);
      setSelectedDistrict(null);
      setCompanyType('person');
    } catch (err: any) {
      let errorMessage = 'Error al crear el cliente';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ');
        } else {
          errorMessage = JSON.stringify(detail);
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 } }}>
      <Paper elevation={3} sx={{ borderRadius: { xs: 0, sm: 2 }, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 3, px: { xs: 2, sm: 3 } }}>
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: '1.5rem', sm: '2rem' },
              fontWeight: 700,
            }}
          >
            Crear Nuevo Cliente
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            Complete la información del cliente
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          {success && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {loadingData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Cargando datos...</Typography>
            </Box>
          ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Tipo de documento y número */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                  📄 Documento de Identidad
                </Typography>
                <Stack spacing={2}>
                  <Autocomplete
                    options={documentTypes}
                    getOptionLabel={(option) => option.name}
                    value={selectedDocType}
                    onChange={(_, newValue) => setSelectedDocType(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tipo de Documento *"
                        placeholder="Seleccionar..."
                        size="medium"
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      fullWidth
                      label="Número de Documento *"
                      value={docNumber}
                      onChange={(e) => handleDocNumberChange(e.target.value)}
                      placeholder="Ej: 12345678"
                      size="medium"
                      error={!!docNumberError}
                      helperText={docNumberError || (selectedDocType?.l10n_pe_vat_code === '1' ? 'DNI: 8 dígitos' : selectedDocType?.l10n_pe_vat_code === '6' ? 'RUC: 11 dígitos' : '')}
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*'
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleSearchDocument}
                      disabled={searching || !docNumber || !selectedDocType || !!docNumberError}
                      startIcon={searching ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                      sx={{
                        minWidth: { xs: '100%', sm: '160px' },
                        height: '56px',
                        fontWeight: 600
                      }}
                    >
                      {searching ? 'Buscando...' : 'Buscar SUNAT'}
                    </Button>
                  </Stack>
                </Stack>
              </Box>

              <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                  👤 Información Personal
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label={companyType === 'company' ? 'Razón Social *' : 'Nombre Completo *'}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    size="medium"
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      fullWidth
                      label="Teléfono"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="999999999"
                      size="medium"
                    />
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="correo@ejemplo.com"
                      size="medium"
                    />
                  </Stack>
                  <FormControl fullWidth size="medium">
                    <InputLabel>Tipo de Cliente</InputLabel>
                    <Select
                      value={companyType}
                      onChange={(e) => setCompanyType(e.target.value as 'person' | 'company')}
                      label="Tipo de Cliente"
                      disabled={selectedDocType?.l10n_pe_vat_code === '6'}
                    >
                      <MenuItem value="person">Persona Natural</MenuItem>
                      <MenuItem value="company">Empresa</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Box>

              <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                  📍 Dirección
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Dirección *"
                    value={street}
                    onChange={(e) => setStreet(e.target.value)}
                    placeholder="Calle / Av. / Jr. ..."
                    multiline
                    rows={2}
                    size="medium"
                    required
                  />
                  <Autocomplete
                    options={states}
                    getOptionLabel={(option) => option.name}
                    value={selectedState}
                    onChange={(_, newValue) => setSelectedState(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Departamento *"
                        placeholder="Seleccionar..."
                        size="medium"
                        required
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Autocomplete
                      fullWidth
                      options={cities}
                      getOptionLabel={(option) => option.name}
                      value={selectedCity}
                      onChange={(_, newValue) => setSelectedCity(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Provincia *"
                          placeholder="Seleccionar..."
                          size="medium"
                          required
                        />
                      )}
                      disabled={!selectedState}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                    <Autocomplete
                      fullWidth
                      options={districts}
                      getOptionLabel={(option) => option.name}
                      value={selectedDistrict}
                      onChange={(_, newValue) => setSelectedDistrict(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Distrito *"
                          placeholder="Seleccionar..."
                          size="medium"
                          required
                        />
                      )}
                      disabled={!selectedCity}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                  </Stack>
                </Stack>
              </Box>

              <Button
                type="submit"
                variant="contained"
                color="success"
                fullWidth
                size="large"
                disabled={loading}
                sx={{
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  mt: 2,
                  boxShadow: 3,
                  '&:hover': { boxShadow: 6 }
                }}
              >
                {loading ? <CircularProgress size={26} color="inherit" /> : 'Crear Cliente'}
              </Button>
            </Stack>
          </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default CustomerForm;